/**
 * ClimateChangeTile — "Damals vs. Heute" tile on the landing dashboard.
 *
 * Pulls /api/heritage/landing (cached 24h) and renders a three-anchor
 * comparison of the last 30 days vs. the same 30 days 30 years ago.
 *
 * Visual: oversized delta arrow + temperature shift; cycles through the
 * three anchor points every 6 seconds to keep the tile lively without
 * the user having to interact.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowDown, Clock, ThermometerSimple } from "@phosphor-icons/react";
import StatusTile from "./StatusTile";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function deltaTone(deltaC) {
  if (deltaC == null) return "#F5F5F5";
  if (deltaC >= 1.0) return "#FF5500";  // significantly warmer
  if (deltaC >= 0.3) return "#FFC24B";  // slightly warmer
  if (deltaC <= -0.3) return "#8B7CF6"; // cooler
  return "#00FA9A";                      // stable
}

function deltaLabel(deltaC) {
  if (deltaC == null) return "keine Daten";
  if (deltaC >= 1.5) return "deutlich wärmer";
  if (deltaC >= 0.5) return "wärmer";
  if (deltaC <= -0.5) return "kühler";
  return "ähnlich";
}

export default function ClimateChangeTile() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/heritage/landing`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setError(true); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data?.anchors?.length) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % data.anchors.length);
    }, 6000);
    return () => clearInterval(t);
  }, [data]);

  if (error || (data && !data.anchors?.length)) {
    return (
      <StatusTile
        label="Damals vs. Heute"
        fetchedAt={data?.fetched_at}
        accentColor="#D9A87C"
        className="min-h-[200px]"
        testid="tile-climate"
      >
        <div className="text-white/60 text-sm">ERA5-Archiv aktuell nicht erreichbar.</div>
      </StatusTile>
    );
  }

  if (!data) {
    return (
      <StatusTile
        label="Damals vs. Heute"
        accentColor="#D9A87C"
        className="min-h-[200px]"
        testid="tile-climate"
      >
        <div className="h-20 bg-white/5 animate-pulse rounded-sm" />
      </StatusTile>
    );
  }

  const a = data.anchors[idx];
  const Arrow = (a.delta_temp_c ?? 0) >= 0 ? ArrowUp : ArrowDown;
  const tone = deltaTone(a.delta_temp_c);
  const label = deltaLabel(a.delta_temp_c);
  const delta = a.delta_temp_c;

  return (
    <StatusTile
      label="Damals vs. Heute"
      fetchedAt={data.fetched_at}
      accentColor="#D9A87C"
      to="/heritage"
      className="min-h-[200px] overflow-hidden"
      testid="tile-climate"
    >
      <div className="flex items-center gap-2 text-white/60 text-xs font-mono uppercase tracking-widest">
        <Clock size={14} weight="fill" className="text-[#D9A87C]" />
        30 Tage vs. vor 30 Jahren
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={a.anchor_id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="flex flex-col gap-1"
        >
          <div className="flex items-baseline gap-2">
            <div className="font-bold text-5xl leading-none flex items-center gap-1" style={{ color: tone }}>
              <Arrow size={36} weight="bold" />
              {delta == null ? "—" : (delta > 0 ? "+" : "") + delta.toFixed(1)}
              <span className="text-2xl font-semibold opacity-70 ml-0.5">°C</span>
            </div>
          </div>
          <div className="text-white text-base font-semibold leading-tight uppercase tracking-wide" style={{ color: tone }}>
            {label}
          </div>
          <div className="text-sm text-white/70 leading-tight">
            <span className="font-medium">{a.label}</span> · heute Ø{" "}
            <span className="font-mono">{a.today_temp_c ?? "—"}</span> °C, damals{" "}
            <span className="font-mono">{a.then_temp_c ?? "—"}</span> °C
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-auto flex items-center gap-1.5">
        {data.anchors.map((_, i) => (
          <span
            key={i}
            className={`h-1 transition-all duration-300 ${
              i === idx ? "w-6 bg-[#7A4A2D]" : "w-2 bg-white/10"
            }`}
          />
        ))}
        <span className="ml-auto text-[10px] text-white/40 font-mono">ERA5 Archiv</span>
      </div>
    </StatusTile>
  );
}
